// Original SVG item artwork, drawn in the style of the classic game's small
// item pictures. Each item names an "icon" key; drop a PNG into
// client/public/assets/items/<itemId>.png (and list it in the manifest) to replace one.
import { useId, type ReactNode } from 'react';

const METAL = ['#e8e8e8', '#9a9a9a', '#4a4a4a'];

function Defs({ id, a, b, c }: { id: string; a: string; b: string; c: string }) {
  return (
    <defs>
      <linearGradient id={`${id}m`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor={a} />
        <stop offset="0.5" stopColor={b} />
        <stop offset="1" stopColor={c} />
      </linearGradient>
      <linearGradient id={`${id}w`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#a0622d" />
        <stop offset="1" stopColor="#4a2610" />
      </linearGradient>
      <radialGradient id={`${id}bg`} cx="0.5" cy="0.4" r="0.7">
        <stop offset="0" stopColor="#2b2b2b" />
        <stop offset="1" stopColor="#070707" />
      </radialGradient>
    </defs>
  );
}

type Drawer = (m: string, w: string, tint: string, idx: number) => ReactNode;

// Shapes are drawn in a 64x64 box.
const DRAW: Record<string, Drawer> = {
  // v0.8
  cane: (m) => (
    <g stroke="#111" strokeWidth="1">
      <path d="M44 10 q8 0 8 8" fill="none" stroke="#d4a017" strokeWidth="4" strokeLinecap="round" />
      <rect x="41" y="12" width="5" height="44" rx="2" transform="rotate(14 43 34)" fill="#2a1a0a" />
      <path d="M40 14 l-22 42" stroke={m} strokeWidth="2" opacity="0.9" />
      <rect x="40" y="16" width="7" height="3" fill="#d4a017" transform="rotate(14 43 17)" />
    </g>
  ),
  hat: (_m, _w, t) => (
    <g stroke="#111" strokeWidth="1">
      <ellipse cx="32" cy="42" rx="26" ry="7" fill="#2a2a2a" />
      <path d="M16 42 q0 -22 16 -22 q16 0 16 22z" fill={t === '#222' ? '#3a3a3a' : t} />
      <path d="M24 22 q8 6 16 0" fill="none" stroke="#111" />
      <rect x="16" y="34" width="32" height="5" fill="#7a1a10" />
    </g>
  ),
  ring: () => (
    <g stroke="#111" strokeWidth="1">
      <ellipse cx="32" cy="40" rx="16" ry="12" fill="none" stroke="#d4a017" strokeWidth="6" />
      <ellipse cx="32" cy="40" rx="16" ry="12" fill="none" stroke="#fff3b0" strokeWidth="1" opacity="0.6" />
      <rect x="22" y="16" width="20" height="14" rx="3" fill="#b8860b" />
      <path d="M26 20 h12 M26 24 h12" stroke="#5a3a00" strokeWidth="1.5" />
    </g>
  ),
  trophy: () => (
    <g stroke="#3a2600" strokeWidth="1.2">
      <path d="M20 12 h24 v10 q0 14 -12 16 q-12 -2 -12 -16z" fill="#f2b833" />
      <path d="M20 16 h-6 q0 10 8 12 M44 16 h6 q0 10 -8 12" fill="none" strokeWidth="2.5" stroke="#c47a0a" />
      <rect x="29" y="38" width="6" height="8" fill="#c47a0a" />
      <rect x="20" y="46" width="24" height="6" fill="#5a3a1e" />
      <path d="M26 18 q2 10 6 12" fill="none" stroke="#fff6c0" strokeWidth="1.5" />
    </g>
  ),
  knuckles: (m) => (
    <g fill={m} stroke="#222" strokeWidth="1">
      <path d="M12 26h40v8c0 8-6 12-12 12h-16c-6 0-12-4-12-12z" />
      {[0, 1, 2, 3].map((i) => <circle key={i} cx={17 + i * 10} cy={24} r={6} fill="#111" stroke={m} strokeWidth="3.5" />)}
    </g>
  ),
  knife: (m, w) => (
    <g stroke="#111" strokeWidth="1">
      <path d="M8 40 L40 22 L50 20 L44 28 L14 44z" fill={m} />
      <path d="M40 22 L58 14 L56 20 L46 26z" fill={w} />
      <circle cx="51" cy="18" r="1.4" fill="#ddd" />
    </g>
  ),
  crowbar: (m) => (
    <g fill="none" stroke={m} strokeWidth="5" strokeLinecap="round">
      <path d="M12 52 L46 18 q6-6 10 0" />
      <path d="M12 52 l-4 2" strokeWidth="4" />
      <path d="M12 52 L46 18" stroke="#c22" strokeWidth="2" strokeDasharray="3 9" />
    </g>
  ),
  bat: (_m, w) => <path d="M8 54 L12 50 L44 14 q6-6 10 0 q4 6-2 10 L16 56z" fill={w} stroke="#2a1505" strokeWidth="1.2" />,
  pistol: (m, w) => (
    <g stroke="#111" strokeWidth="1">
      <rect x="10" y="20" width="44" height="10" rx="2" fill={m} />
      <path d="M30 30 h12 l4 22 h-12z" fill={w} />
      <path d="M24 30 q0 10 8 10" fill="none" stroke={m} strokeWidth="2.5" />
      <rect x="12" y="22" width="30" height="2" fill="#fff" opacity="0.4" stroke="none" />
    </g>
  ),
  revolver: (m, w) => (
    <g stroke="#111" strokeWidth="1">
      <rect x="6" y="22" width="30" height="6" rx="1.5" fill={m} />
      <rect x="32" y="19" width="14" height="13" rx="3" fill={m} />
      <circle cx="39" cy="25.5" r="5" fill="#333" stroke={m} />
      <path d="M44 28 q6 2 6 10 l-2 16 h-10 l2-16 q0-6 4-10z" fill={w} />
      <path d="M34 32 q0 8 6 8" fill="none" stroke={m} strokeWidth="2.5" />
    </g>
  ),
  shotgun: (m, w) => (
    <g stroke="#111" strokeWidth="1">
      <rect x="4" y="24" width="38" height="5" rx="1" fill={m} />
      <rect x="4" y="29" width="30" height="3" rx="1" fill={m} />
      <rect x="14" y="30" width="12" height="5" rx="2" fill={w} />
      <path d="M40 22 h8 l12 8 l-2 10 l-14 -6 h-4z" fill={w} />
    </g>
  ),
  tommy: (m, w) => (
    <g stroke="#111" strokeWidth="1">
      <rect x="4" y="24" width="30" height="5" rx="1" fill={m} />
      <rect x="20" y="21" width="22" height="10" rx="2" fill={m} />
      <circle cx="30" cy="40" r="8" fill={m} />
      <circle cx="30" cy="40" r="3" fill="#333" />
      <path d="M14 29 l-2 10 h5 l2-10z" fill={w} />
      <path d="M42 22 h4 l14 6 l-2 10 l-12 -5 h-4z" fill={w} />
      <path d="M36 31 l-2 9 h5 l1-9z" fill={w} />
    </g>
  ),
  rifle: (m, w) => (
    <g stroke="#111" strokeWidth="1">
      <rect x="2" y="25" width="34" height="4" rx="1" fill={m} />
      <rect x="22" y="22" width="20" height="9" rx="1" fill={m} />
      <path d="M30 31 l-4 12 h6 l3-12z" fill="#2a2a2a" />
      <path d="M42 23 h4 l14 4 l-1 11 l-13 -4 h-4z" fill={w} />
      <rect x="12" y="29" width="10" height="4" rx="1" fill={w} />
    </g>
  ),
  sniper: (m, w) => (
    <g stroke="#111" strokeWidth="1">
      <rect x="2" y="28" width="38" height="3.5" rx="1" fill={m} />
      <rect x="22" y="25" width="18" height="9" rx="1" fill={m} />
      <rect x="18" y="17" width="20" height="5" rx="2.5" fill="#222" stroke={m} />
      <path d="M26 22 v3 M32 22 v3" stroke={m} strokeWidth="2" />
      <path d="M40 26 h5 l15 4 l-2 12 l-13 -5 h-5z" fill={w} />
      <path d="M8 31 l-3 8 M10 31 l3 8" stroke={m} strokeWidth="1.5" />
    </g>
  ),
  launcher: (m, w) => (
    <g stroke="#111" strokeWidth="1">
      <rect x="4" y="20" width="44" height="12" rx="3" fill="#5a6a3a" />
      <circle cx="8" cy="26" r="5" fill="#222" stroke={m} />
      <path d="M30 32 l-2 12 h6 l2-12z" fill={w} />
      <path d="M48 22 h8 v8 h-8z" fill={m} />
    </g>
  ),
  rpg: (m, w) => (
    <g stroke="#111" strokeWidth="1">
      <rect x="14" y="24" width="44" height="7" rx="2" fill="#4a5a2a" />
      <path d="M14 22 l-10 5.5 l10 5.5z" fill="#6a7a3a" />
      <path d="M2 27.5 l4 -3 v6z" fill={m} />
      <path d="M30 31 l-2 12 h6 l2-12z" fill={w} />
      <path d="M44 31 l-1 9 h5 l1-9z" fill={w} />
    </g>
  ),
  minigun: (m) => (
    <g stroke="#111" strokeWidth="1">
      {[0, 1, 2, 3].map((i) => <rect key={i} x="2" y={18 + i * 4} width="34" height="3" rx="1" fill={m} />)}
      <rect x="32" y="16" width="20" height="20" rx="4" fill={m} />
      <path d="M44 36 l-2 14 h8 l2-14z" fill="#333" />
      <circle cx="52" cy="26" r="6" fill="#333" stroke={m} />
    </g>
  ),
  jacket: (_m, _w, tint) => (
    <g stroke="#111" strokeWidth="1">
      <path d="M20 10 l12 6 l12 -6 l10 6 l4 36 h-10 l-2 -22 v28 h-28 v-28 l-2 22 h-10 l4 -36z" fill={tint} />
      <path d="M32 16 v38" stroke="#000" />
      <path d="M26 14 l6 12 l6 -12" fill="none" stroke="#000" strokeWidth="1.5" />
    </g>
  ),
  coat: (_m, _w, tint) => (
    <g stroke="#111" strokeWidth="1">
      <path d="M20 8 l12 6 l12 -6 l10 6 l4 42 h-10 l-2 -26 v34 h-28 v-34 l-2 26 h-10 l4 -42z" fill={tint} />
      <path d="M26 12 l6 16 l6 -16" fill="#222" />
      <circle cx="30" cy="36" r="1.5" fill="#bbb" /><circle cx="30" cy="44" r="1.5" fill="#bbb" />
    </g>
  ),
  vest: (_m, _w, tint) => (
    <g stroke="#111" strokeWidth="1">
      <path d="M18 10 h8 q6 8 12 0 h8 l4 10 v34 h-36 v-34z" fill={tint} />
      <path d="M18 30 h28 M18 40 h28" stroke="#000" opacity="0.6" />
      <rect x="22" y="24" width="8" height="5" fill="#000" opacity="0.4" />
    </g>
  ),
  armor: (m, _w, tint) => (
    <g stroke="#111" strokeWidth="1">
      <path d="M16 10 h10 q6 8 12 0 h10 l4 12 v32 h-40 v-32z" fill={tint} />
      <rect x="20" y="24" width="24" height="24" rx="2" fill={m} opacity="0.85" />
      <path d="M20 36 h24 M32 24 v24" stroke="#333" />
    </g>
  ),
  riot: (m, _w, tint) => (
    <g stroke="#111" strokeWidth="1">
      <path d="M18 24 a14 14 0 0 1 28 0 v6 h-28z" fill={tint} />
      <rect x="20" y="24" width="24" height="7" fill="#7af" opacity="0.6" />
      <rect x="10" y="34" width="44" height="22" rx="3" fill={m} opacity="0.75" />
      <text x="32" y="49" fontSize="8" textAnchor="middle" fill="#111" fontWeight="bold">RIOT</text>
    </g>
  ),
  suit: (_m, _w, tint) => (
    <g stroke="#111" strokeWidth="1">
      <path d="M18 8 l14 6 l14 -6 l10 6 l4 42 h-56 l4 -42z" fill={tint} />
      <path d="M26 12 l6 18 l6 -18" fill="#eee" />
      <path d="M31 16 l1 14 l1 -14z" fill="#a00" />
    </g>
  ),
  sedan: (m, _w, tint) => (
    <g stroke="#111" strokeWidth="1">
      <path d="M4 40 v-8 l8 -2 l8 -10 h22 l8 10 l10 2 v8z" fill={tint} />
      <path d="M22 22 h9 v8 h-15z M34 22 h7 l6 8 h-13z" fill="#9cf" opacity="0.7" />
      <circle cx="16" cy="42" r="6" fill="#111" stroke={m} strokeWidth="2" />
      <circle cx="48" cy="42" r="6" fill="#111" stroke={m} strokeWidth="2" />
    </g>
  ),
  limo: (m, _w, tint) => (
    <g stroke="#111" strokeWidth="1">
      <path d="M2 40 v-7 l6 -2 l6 -8 h36 l6 8 l6 2 v7z" fill={tint} />
      <path d="M16 25 h12 v6 h-16z M30 25 h12 v6 h-12z M44 25 h5 l4 6 h-9z" fill="#333" />
      <circle cx="12" cy="42" r="5" fill="#111" stroke={m} strokeWidth="2" />
      <circle cx="52" cy="42" r="5" fill="#111" stroke={m} strokeWidth="2" />
    </g>
  ),
  armored: (m, _w, tint) => (
    <g stroke="#111" strokeWidth="1">
      <path d="M4 42 v-16 l6 -6 h34 l8 8 l8 2 v12z" fill={tint} />
      <rect x="12" y="24" width="10" height="5" fill="#333" /><rect x="26" y="24" width="10" height="5" fill="#333" />
      {[10, 20, 30, 40, 50].map((x) => <circle key={x} cx={x} cy="34" r="1" fill={m} />)}
      <circle cx="16" cy="44" r="6" fill="#111" stroke={m} strokeWidth="2" />
      <circle cx="48" cy="44" r="6" fill="#111" stroke={m} strokeWidth="2" />
    </g>
  ),
  truck: (m, _w, tint) => (
    <g stroke="#111" strokeWidth="1">
      <rect x="4" y="16" width="34" height="24" fill="#ddd" />
      <path d="M38 22 h12 l8 10 v8 h-20z" fill={tint} />
      <path d="M42 25 h7 l5 7 h-12z" fill="#9cf" />
      <circle cx="14" cy="42" r="5" fill="#111" stroke={m} strokeWidth="2" />
      <circle cx="48" cy="42" r="5" fill="#111" stroke={m} strokeWidth="2" />
    </g>
  ),
  motorcycle: (m, _w, tint) => (
    <g stroke="#111" strokeWidth="1">
      <circle cx="14" cy="42" r="9" fill="none" stroke={m} strokeWidth="3" />
      <circle cx="50" cy="42" r="9" fill="none" stroke={m} strokeWidth="3" />
      <path d="M14 42 l12 -12 h14 l10 12 M40 30 l4 -10 h6" fill="none" stroke={m} strokeWidth="2.5" />
      <path d="M24 30 q8 -8 18 0z" fill={tint} />
    </g>
  ),
  boat: (_m, _w, tint) => (
    <g stroke="#111" strokeWidth="1">
      <path d="M4 36 h56 l-8 12 h-40z" fill={tint} />
      <path d="M22 36 l6 -10 h14 l4 10z" fill="#eee" />
      <path d="M2 52 q8 -4 15 0 t15 0 t15 0 t15 0" fill="none" stroke="#39c" strokeWidth="2" />
    </g>
  ),
  helicopter: (m, _w, tint) => (
    <g stroke="#111" strokeWidth="1">
      <path d="M4 14 h56" stroke={m} strokeWidth="2.5" />
      <path d="M32 14 v6" stroke={m} strokeWidth="2" />
      <path d="M14 36 q0 -16 18 -16 h6 q10 0 10 10 v6z" fill={tint} />
      <path d="M48 30 h12 l2 -6" fill="none" stroke={tint} strokeWidth="4" />
      <path d="M20 30 q0 -7 8 -7 v7z" fill="#9cf" />
      <path d="M14 44 h30 M20 36 v8 M38 36 v8" stroke={m} strokeWidth="2" />
    </g>
  ),
  jet: (m, _w, tint) => (
    <g stroke="#111" strokeWidth="1">
      <path d="M4 32 q4 -6 14 -6 h34 l8 -10 h4 l-4 14 q0 4 -6 4 h-40 q-10 0 -10 -2z" fill={tint} />
      <path d="M26 30 l10 14 h6 l-4 -14z" fill={m} />
      {[14, 20, 26, 32, 38].map((x) => <circle key={x} cx={x} cy="29" r="1.3" fill="#39c" />)}
    </g>
  ),
  gascan: () => (
    <g stroke="#111" strokeWidth="1">
      <path d="M14 18 h28 l8 8 v28 h-36z" fill="#c22" />
      <path d="M42 18 l6 -8 h6 v6 l-6 4" fill="#c22" />
      <path d="M20 26 l20 20 M40 26 l-20 20" stroke="#800" strokeWidth="3" />
    </g>
  ),
  lockpick: (m) => (
    <g stroke={m} strokeWidth="2.5" strokeLinecap="round" fill="none">
      <path d="M8 46 L44 18 l6 2 l2 -6" />
      <path d="M10 52 L48 28 l4 4" />
      <rect x="4" y="44" width="10" height="12" rx="3" fill="#432" stroke="#111" strokeWidth="1" />
    </g>
  ),
  photo: () => (
    <g stroke="#111" strokeWidth="1">
      <rect x="12" y="14" width="36" height="30" fill="#eee" transform="rotate(-8 30 30)" />
      <rect x="18" y="20" width="36" height="30" fill="#f4f0e6" />
      <rect x="21" y="23" width="30" height="20" fill="#556" />
      <circle cx="30" cy="31" r="4" fill="#ccb" /><path d="M24 43 q6 -10 12 0z" fill="#ccb" />
    </g>
  ),
  papers: () => (
    <g stroke="#111" strokeWidth="1">
      <rect x="14" y="10" width="32" height="42" fill="#efe6c8" />
      {[18, 24, 30, 36].map((y) => <path key={y} d={`M18 ${y} h24`} stroke="#999" />)}
      <circle cx="38" cy="44" r="6" fill="#a22" />
    </g>
  ),
  dynamite: () => (
    <g stroke="#111" strokeWidth="1">
      {[0, 1, 2].map((i) => <rect key={i} x={14 + i * 12} y="22" width="11" height="32" rx="2" fill="#c33" />)}
      <rect x="12" y="34" width="40" height="5" fill="#333" />
      <path d="M32 22 q4 -10 14 -12" fill="none" stroke="#888" strokeWidth="1.5" />
      <circle cx="47" cy="10" r="3" fill="#fc3" stroke="none" />
    </g>
  ),
  ledger: () => (
    <g stroke="#111" strokeWidth="1">
      <rect x="12" y="10" width="38" height="44" rx="2" fill="#2a4a2a" />
      <rect x="16" y="10" width="4" height="44" fill="#1a2a1a" />
      <rect x="26" y="20" width="18" height="8" fill="#c9b37a" />
    </g>
  ),
  briefcase: () => (
    <g stroke="#111" strokeWidth="1">
      <rect x="8" y="20" width="48" height="32" rx="3" fill="#5a3a1a" />
      <path d="M24 20 v-6 h16 v6" fill="none" stroke="#333" strokeWidth="3" />
      <rect x="28" y="30" width="8" height="6" fill="#d4a017" />
      <text x="32" y="50" fontSize="9" textAnchor="middle" fill="#6c3">$$$</text>
    </g>
  ),
  sandwich: () => (
    <g stroke="#111" strokeWidth="1">
      <path d="M8 40 q24 -26 48 0z" fill="#c8964a" />
      <rect x="8" y="38" width="48" height="6" fill="#b03a3a" />
      <rect x="8" y="43" width="48" height="4" fill="#d4627a" />
      <path d="M8 47 h48 v4 q-24 6 -48 0z" fill="#c8964a" />
      <path d="M14 39 q4 -3 8 0 q4 -3 8 0 q4 -3 8 0 q4 -3 8 0 q4 -3 8 0" stroke="#5a8a2a" strokeWidth="2" fill="none" />
      {[18, 28, 40].map((x) => <circle key={x} cx={x} cy="30" r="1" fill="#6a4a1a" />)}
    </g>
  ),
  // collection pieces: icon key is "<group>:<index>"
  charm: (_m, _w, _t, i) => <g>{[<path key="r" d="M28 52 q-8-18 0-34 q6-6 8 2 q4 16-2 32z" fill="#ddd" />, <g key="c" fill="#3a3">{[0, 90, 180, 270].map((a) => <circle key={a} cx={32 + 9 * Math.cos((a * Math.PI) / 180)} cy={30 + 9 * Math.sin((a * Math.PI) / 180)} r="8" />)}<path d="M32 38 l2 16" stroke="#3a3" strokeWidth="2" /></g>, <path key="h" d="M16 50 v-18 a16 16 0 0 1 32 0 v18 h-8 v-18 a8 8 0 0 0 -16 0 v18z" fill="#999" />, <circle key="d" cx="32" cy="32" r="16" fill="#ccc" stroke="#888" strokeWidth="2" />, <g key="m"><circle cx="32" cy="32" r="16" fill="#d4a017" /><path d="M32 22 v20 M24 30 h16" stroke="#7a5a00" strokeWidth="2" /></g>, <path key="w" d="M32 50 v-20 l-12 -16 M32 30 l12 -16" stroke="#eed" strokeWidth="4" fill="none" strokeLinecap="round" />, <g key="x"><rect x="12" y="20" width="20" height="20" rx="3" fill="#eee" /><rect x="30" y="28" width="20" height="20" rx="3" fill="#fee" /><circle cx="22" cy="30" r="2" /><circle cx="40" cy="38" r="2" /></g>][i % 7]}</g>,
  cigar: (_m, _w, _t, i) => (
    <g stroke="#111" strokeWidth="1" transform={`rotate(${-20 + i * 6} 32 32)`}>
      <rect x="8" y={28 - (i % 3)} width={40 + i * 2} height={8 + (i % 3) * 2} rx="4" fill="#6a3a1a" />
      <rect x="30" y={27 - (i % 3)} width="8" height={10 + (i % 3) * 2} fill={['#c22', '#d4a017', '#228', '#c22', '#d4a017', '#282', '#888'][i]} />
      <circle cx={48 + i * 2} cy="32" r="3" fill="#f60" opacity="0.8" />
    </g>
  ),
  cufflink: (_m, _w, _t, i) => {
    const c = ['#ccc', '#d4a017', '#222', '#f5f0e6', '#c11', '#14c', '#bdf'][i];
    return (
      <g stroke="#111" strokeWidth="1">
        <circle cx="22" cy="30" r="11" fill="#d4a017" /><circle cx="22" cy="30" r="7" fill={c} />
        <circle cx="44" cy="36" r="11" fill="#d4a017" /><circle cx="44" cy="36" r="7" fill={c} />
      </g>
    );
  },
  watch: (_m, _w, _t, i) => (
    <g stroke="#111" strokeWidth="1">
      <circle cx="32" cy="36" r="18" fill={i % 2 ? '#d4a017' : '#bbb'} />
      <circle cx="32" cy="36" r="14" fill="#f4f0e6" />
      <path d={`M32 36 l0 -10 M32 36 l${6 + i} 4`} stroke="#111" strokeWidth="1.5" />
      <rect x="29" y="12" width="6" height="6" rx="2" fill="#d4a017" />
    </g>
  ),
  record: (_m, _w, _t, i) => (
    <g>
      <circle cx="32" cy="32" r="24" fill="#111" stroke="#333" />
      <circle cx="32" cy="32" r="18" fill="none" stroke="#2a2a2a" /><circle cx="32" cy="32" r="13" fill="none" stroke="#2a2a2a" />
      <circle cx="32" cy="32" r="8" fill={['#c22', '#14c', '#d4a017', '#282', '#c6c', '#f80', '#eee'][i]} />
      <circle cx="32" cy="32" r="1.5" fill="#000" />
    </g>
  ),
  bottle: (_m, _w, _t, i) => (
    <g stroke="#111" strokeWidth="1">
      <path d="M26 8 h12 v12 q8 4 8 14 v22 h-28 v-22 q0 -10 8 -14z" fill={['#8a4a10', '#a06020', '#ddd', '#c84', '#eee', '#ee2', '#6a2a00'][i]} opacity="0.9" />
      <rect x="20" y="34" width="24" height="12" fill="#f4f0e6" />
      <rect x="26" y="4" width="12" height="6" fill="#d4a017" />
    </g>
  ),
  painting: (_m, _w, _t, i) => (
    <g stroke="#111" strokeWidth="1">
      <rect x="8" y="12" width="48" height="40" fill="#b8860b" />
      <rect x="13" y="17" width="38" height="30" fill={['#4a6a8a', '#8a6a4a', '#6a8a4a', '#a33', '#ccc', '#7a6a50', '#2a3a5a'][i]} />
      <path d="M13 40 l12 -10 l8 6 l8 -8 l10 12 v7 h-38z" fill="#000" opacity="0.35" />
    </g>
  ),
  gem: (_m, _w, _t, i) => {
    const c = ['#2c6', '#c12', '#24c', '#ccd', '#fc3', '#f9c', '#6cf'][i];
    return (
      <g stroke="#111" strokeWidth="1">
        <path d="M16 24 l8 -10 h16 l8 10 l-16 26z" fill={c} />
        <path d="M16 24 h32 M24 14 l8 10 l8 -10 M32 24 v26" stroke="#fff" opacity="0.5" fill="none" />
      </g>
    );
  },
  crown: (_m, _w, _t, i) => (
    <g stroke="#111" strokeWidth="1">
      <path d="M10 46 l-2 -24 l12 10 l12 -16 l12 16 l12 -10 l-2 24z" fill="#d4a017" />
      <rect x="10" y="46" width="44" height="6" fill="#b8860b" />
      <circle cx="32" cy="40" r="3" fill={['#c12', '#24c', '#2c6', '#fff', '#c12', '#24c', '#2c6'][i]} />
    </g>
  ),
};

const RARITY_FRAME: Record<string, string> = { uncommon: '#6c3', rare: '#39f', epic: '#c6f', legendary: '#ff9933' };

const TINTS = ['#2a2a2a', '#3a2a1a', '#1a2a3a', '#4a1a1a', '#2a3a2a', '#3a3a4a', '#5a4a2a', '#222'];

function hash(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

export function ItemArt({ icon, seed, size = 40, rarity }: { icon: string; seed: string; size?: number; rarity?: string }) {
  const id = useId().replace(/:/g, '');
  const [key, idxStr] = icon.split(':');
  const draw = DRAW[key] ?? DRAW.briefcase;
  const h = hash(seed);
  const tint = TINTS[h % TINTS.length];
  const gold = rarity === 'epic' || rarity === 'rare' || rarity === 'legendary' || rarity === 'unique';
  const metal = gold && h % 2 ? ['#fff3b0', '#d4a017', '#6a4a00'] : METAL;
  return (
    <svg className="icon item-icon" width={size} height={size} viewBox="0 0 64 64" aria-hidden="true">
      <Defs id={id} a={metal[0]} b={metal[1]} c={metal[2]} />
      <rect width="64" height="64" fill={`url(#${id}bg)`} />
      {draw(`url(#${id}m)`, `url(#${id}w)`, tint, Number(idxStr ?? h % 7))}
      {rarity && rarity !== 'common' && rarity !== 'unique' && (
        <rect x="1" y="1" width="62" height="62" fill="none" strokeWidth={rarity === 'legendary' ? 3 : 2} stroke={RARITY_FRAME[rarity] ?? '#6c3'} opacity={rarity === 'legendary' ? 0.95 : 0.7} />
      )}
      {rarity === 'unique' && (
        <g fill="none">
          <rect x="1.5" y="1.5" width="61" height="61" stroke="#ffcc33" strokeWidth="3" />
          <rect x="5" y="5" width="54" height="54" stroke="#c22" strokeWidth="1" opacity="0.9" />
          {[[4, 4], [60, 4], [4, 60], [60, 60]].map(([x, y]) => <circle key={`${x}${y}`} cx={x} cy={y} r="2.5" fill="#ffcc33" stroke="#7a5a00" strokeWidth="0.6" />)}
        </g>
      )}
    </svg>
  );
}
